import { WithStyles, withStyles } from "@material-ui/core/styles";
import React from "react";
import Moment from "moment";
import { Game } from "../../types/game";
import RangeGameBadge from "./RangeGameBadge";
import { ENABLE_RHI } from "../../config/featureFlags";
import { ScoreCard } from "../../types/scoreCard";
import { Gender } from "../../types/user";
import { isRangeGame } from "../../utils/rhiGame";
import { rangeGameDetailsStyles } from "./rangeGameStyles";
import GameCodeCapsule from "./GameCodeCapsule";

interface GameDetailsProps extends WithStyles<typeof styles> {
  game: Game;
  hideTeeInfo?: boolean;
  showEntryFee?: boolean;
}

export const getTeeDetails = (game: Game): string[] => {
  let output: string[] = [];
  const cards: ScoreCard[] = Object.values(game.scoreCards);
  cards.forEach(card => {
    let parts = [];
    const totalPar = card.holesParams.reduce((total: number, param) => total + param.par || 0, 0);
    parts.push(card.teeColor);
    if (card.courseRating) parts.push(`CR: ${card.courseRating}`);
    parts.push(`SL: ${card.slope}`);
    parts.push(`Par: ${totalPar}`);
    if (card.gender === Gender.Men) {
      output.push(`Men (${parts.join(", ")})`);
    } else {
      output.push(`Ladies (${parts.join(", ")})`);
    }
  });
  return output;
};

const styles = rangeGameDetailsStyles;

const joinWithDots = (parts: Array<React.ReactNode | null | undefined>, dotClass: string) => {
  const visible = parts.filter(part => part !== null && part !== undefined && part !== "");
  return visible.map((part, i) => (
    <React.Fragment key={i}>
      {i > 0 && <span className={dotClass} aria-hidden="true" />}
      {part}
    </React.Fragment>
  ));
};

const getTeeDisplayLines = (game: Game): string[][] => {
  return Object.values(game.scoreCards).map(card => {
    const totalPar = card.holesParams.reduce((total: number, param) => total + param.par || 0, 0);
    const gender = card.gender === Gender.Men ? "Men" : "Ladies";
    const lead = card.teeColor ? `${gender} (${card.teeColor})` : gender;
    const parts = [lead];
    if (card.courseRating) parts.push(`CR: ${card.courseRating}`);
    parts.push(`SL: ${card.slope}`);
    parts.push(`Par: ${totalPar}`);
    return parts;
  });
};

const GameDetailsView = ({ game, hideTeeInfo, showEntryFee = false, classes }: GameDetailsProps) => {
  const date = Moment(game.date);
  const scoreCard: ScoreCard = Object.values(game.scoreCards)[0];
  const range = isRangeGame(game);
  const teeLines = !hideTeeInfo ? getTeeDisplayLines(game) : [];

  const courseName = scoreCard?.course?.name || null;
  const clubName = scoreCard?.course?.club?.name || null;
  const hasVenue = Boolean(courseName || clubName);

  const playingAs =
    game.competition?.playingRules && game.competition.playingRules.playingAs === "team" ? "Team" : "Individual";
  const holesPart = scoreCard?.course.holesNumber ? `${scoreCard.course.holesNumber} Holes` : null;
  const allowancePart = game.competition?.fullHandicapAllowance
    ? `PH = CH x ${game.competition.fullHandicapAllowance}%`
    : null;

  const dateLine = date.format("ddd DD/MM/YY hh.mm");
  const fee = Number(game.paymentRules?.fee);
  const showFee = showEntryFee && game.isPaid === true && Number.isFinite(fee);

  return (
    <div className={classes.root}>
      <div className={classes.titleRow}>
        <span className={classes.title}>{game.name}</span>
        {ENABLE_RHI && range && <RangeGameBadge game={game} variant="list" />}
        <GameCodeCapsule code={game.code} />
      </div>
      {hasVenue && (
        <div className={`${classes.detailLine} ${classes.venueLine}`}>
          {joinWithDots(
            courseName && clubName && courseName !== clubName ? [courseName, clubName] : [courseName || clubName],
            classes.dot,
          )}
        </div>
      )}
      <div className={`${classes.detailLine} ${classes.whenLine}`}>
        {joinWithDots([dateLine, showFee ? <span className={classes.fee}>{`Entry £${fee.toFixed(2)}`}</span> : null], classes.dot)}
      </div>
      <div className={`${classes.detailLine} ${classes.formatLine}`}>
        {joinWithDots([playingAs, holesPart, allowancePart], classes.dot)}
      </div>
      {teeLines.map((parts, i) => (
        <div
          key={`${parts.join("-")}-${i}`}
          className={`${classes.detailLine} ${classes.teeLine} ${i === 0 ? classes.teeLineFirst : ""}`}
        >
          {joinWithDots(parts, classes.dot)}
        </div>
      ))}
    </div>
  );
};

export const GameDetails = withStyles(styles)(GameDetailsView);
